import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, Layers, Search, ShieldCheck, Sparkles } from 'lucide-react';
import EnrollmentModal from '../components/enrollment/EnrollmentModal';
import { allCourses, Course, courseDetails } from '../data/courses';
import { getPublishedBundles, type Bundle } from '../lib/commerce';
import { getPublishedCourses } from '../lib/lms';
import { Panel, Stamp } from '../components/lms/ui';

interface DisplayCourse extends Course {
  priceValue: number;
  isLms?: boolean;
}

const categoryLabels: Record<string, string> = {
  all: 'All programmes',
  Certificate: 'Certificate',
  Diploma: 'Diploma',
  'Advanced Diploma': 'Advanced Diploma',
  'Mini MBA': 'Mini MBA',
};

const fallbackImage = allCourses[0]?.image || '';

const Courses = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogue, setCatalogue] = useState<DisplayCourse[]>(() =>
    allCourses.map((c) => ({ ...c, priceValue: courseDetails[c.id]?.priceValue ?? 0 }))
  );
  const [selectedCourse, setSelectedCourse] = useState<{
    id: string;
    title: string;
    price: string;
    priceValue: number;
  } | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bundles, setBundles] = useState<Bundle[]>([]);

  useEffect(() => {
    void getPublishedBundles().then(setBundles).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void getPublishedCourses().then((rows) => {
      if (cancelled) return;
      const localIds = new Set(allCourses.map((c) => c.id));
      const extra: DisplayCourse[] = rows
        .filter((c) => !localIds.has(c.id) && c.category !== 'WhatsApp')
        .map((c) => ({
          id: c.id,
          title: c.title,
          category: c.category || 'Program',
          description: c.tagline || c.description || '',
          duration: c.duration || 'Self-paced',
          students: 'New cohort',
          level: c.level || 'All levels',
          rating: 5,
          price: c.price_value === 0 ? 'Free' : `₦${c.price_value.toLocaleString('en-NG')}`,
          image: c.thumbnail || fallbackImage,
          priceValue: c.price_value,
          isLms: true,
        }));
      setCatalogue([
        ...allCourses.map((c) => ({ ...c, priceValue: courseDetails[c.id]?.priceValue ?? 0 })),
        ...extra,
      ]);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');
    if (categoryParam && categoryLabels[categoryParam]) {
      setSelectedCategory(categoryParam);
    }
  }, []);

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(catalogue.map((c) => c.category)))],
    [catalogue]
  );

  const filteredCourses = useMemo(() => {
    let availableCourses = [...catalogue];
    if (selectedCategory !== 'all') {
      availableCourses = availableCourses.filter((course) => course.category === selectedCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      availableCourses = availableCourses.filter(
        (course) =>
          course.title.toLowerCase().includes(q) ||
          course.description.toLowerCase().includes(q) ||
          course.category.toLowerCase().includes(q)
      );
    }
    return availableCourses;
  }, [selectedCategory, searchQuery, catalogue]);

  const handleEnrollClick = (course: DisplayCourse) => {
    if (course.priceValue === 0) {
      navigate(`/course/${course.id}`);
      return;
    }
    setSelectedBundle(null);
    setSelectedCourse({
      id: course.id,
      title: course.title,
      price: course.price,
      priceValue: course.priceValue,
    });
    setIsModalOpen(true);
  };

  const handleBundleClick = (bundle: Bundle) => {
    setSelectedCourse(null);
    setSelectedBundle(bundle);
    setIsModalOpen(true);
  };

  return (
    <div>
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-500 px-6 py-8 text-white shadow-2xl shadow-blue-900/15 sm:px-9 sm:py-10">
        <div className="lms-hero-grid absolute inset-0" aria-hidden="true" />
        <div aria-hidden="true" className="absolute -right-20 -top-24 h-72 w-72 rounded-full border border-white/10" />
        <div aria-hidden="true" className="absolute -bottom-28 right-24 h-56 w-56 rounded-full border border-white/10" />
        <div className="relative max-w-[760px]">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-blue-50">
            <Sparkles className="h-3 w-3" strokeWidth={1.8} /> LSEM programme register · 2026
          </div>
          <h1 className="mt-4 max-w-[20ch] text-[clamp(2rem,4vw,3.2rem)] font-semibold leading-[1.04] tracking-[-0.045em]">
            Build your next export advantage.
          </h1>
          <p className="mt-3 max-w-[58ch] text-[13.5px] leading-6 text-blue-100">
            Practical programmes for people building export-ready businesses. Study at your pace, learn from
            operators, and earn certificates employers can verify.
          </p>

          <label className="relative mt-6 block max-w-[520px]">
            <span className="sr-only">Search programmes</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={1.8} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search programmes, topics or levels"
              className="w-full rounded-xl border border-white/20 bg-white py-3.5 pl-11 pr-4 text-[13px] text-ink shadow-lg shadow-blue-950/10 outline-none transition focus:border-white focus:ring-4 focus:ring-white/20"
            />
          </label>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[8.5px] uppercase tracking-[0.12em] text-blue-100/80">
            <span className="inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" strokeWidth={1.7} /> {catalogue.length} pathways</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.7} /> Verified certificates</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" strokeWidth={1.7} /> Self-paced</span>
          </div>
        </div>
      </section>

      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex max-w-full gap-2 overflow-x-auto pb-1 lg:flex-wrap" role="group" aria-label="Filter programmes by category">
          {categories.map((category) => {
            const active = selectedCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                aria-pressed={active}
                className={`min-h-[36px] flex-none rounded-full border px-3.5 py-2 text-[11.5px] font-semibold transition-all ${
                  active
                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                    : 'border-rule bg-white text-ink-2 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800'
                }`}
              >
                {categoryLabels[category] || category}
              </button>
            );
          })}
        </div>
        <p className="whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
          {filteredCourses.length} {filteredCourses.length === 1 ? 'programme' : 'programmes'} · tuition in naira
        </p>
      </div>

      {filteredCourses.length > 0 ? (
        <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {filteredCourses.map((course) => {
            const free = course.priceValue === 0;
            return (
              <article
                key={course.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-rule bg-white shadow-[0_12px_35px_-30px_rgba(15,40,90,0.4)] transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5"
              >
                <Link to={`/course/${course.id}`} className="relative block h-[168px] overflow-hidden bg-gradient-to-br from-blue-600 to-sky-400">
                  <BookOpen className="absolute left-4 top-4 h-8 w-8 text-white/40" strokeWidth={1.2} />
                  {course.image && (
                    <img
                      src={course.image}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(event) => { event.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/5 to-transparent" />
                  <div className="absolute left-3.5 top-3.5 flex items-center gap-2">
                    <span className="rounded-full bg-white/90 px-2.5 py-1 font-mono text-[8px] font-semibold uppercase tracking-[0.11em] text-blue-800 backdrop-blur">
                      {course.category}
                    </span>
                    {course.isLms && <span className="rounded-full bg-sky-300 px-2.5 py-1 font-mono text-[8px] font-semibold uppercase tracking-[0.11em] text-blue-950">New</span>}
                  </div>
                  <div className="absolute inset-x-4 bottom-3.5">
                    <h2 className="line-clamp-2 text-[17px] font-semibold leading-snug tracking-[-0.02em] text-white">{course.title}</h2>
                  </div>
                </Link>

                <div className="flex flex-1 flex-col p-4">
                  <p className="line-clamp-2 min-h-[40px] text-[12px] leading-5 text-ink-2">{course.description}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 font-mono text-[8.5px] uppercase tracking-[0.1em] text-ink-2">
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-blue-400" strokeWidth={1.7} /> {course.duration}</span>
                    <span className="inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-blue-400" strokeWidth={1.7} /> {course.level}</span>
                  </div>
                  <div className="mt-auto flex items-end justify-between gap-3 border-t border-rule pt-4">
                    <div>
                      <p className="font-mono text-[8px] uppercase tracking-[0.11em] text-ink-2">Tuition</p>
                      <p className="tnum mt-0.5 text-[16px] font-semibold tracking-[-0.02em] text-ink">{course.price}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/course/${course.id}`} className="inline-flex min-h-[36px] items-center rounded-lg px-2.5 text-[11.5px] font-semibold text-blue-700 transition-colors hover:bg-blue-50">
                        Details
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleEnrollClick(course)}
                        className="lms-btn-primary !min-h-[36px] !px-3 !py-2 !text-[11.5px]"
                      >
                        {free ? 'Start free' : 'Enroll'} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.8} />
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="mt-5 rounded-2xl border border-rule bg-white px-6 py-16 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
            <Search className="h-5 w-5" strokeWidth={1.7} />
          </span>
          <p className="mt-4 text-[15px] font-semibold text-ink">No programme matches that search</p>
          <p className="mt-1 text-[13px] text-ink-2">Clear the search or choose another category to see the full register.</p>
          <button
            type="button"
            onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
            className="lms-btn-secondary mt-5"
          >
            Reset filters
          </button>
        </div>
      )}

      {bundles.length > 0 && (
        <section className="mt-10">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-ink">Grouped pathways</h2>
              <p className="mt-1 text-[12.5px] text-ink-2">One payment enrolls you in every programme in the bundle.</p>
            </div>
          </div>
          <div className={`mt-4 grid gap-4 ${bundles.length === 1 ? '' : 'md:grid-cols-2'}`}>
            {bundles.map((bundle) => (
              <Panel key={bundle.id} className="flex flex-col justify-between p-5">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <Stamp tone="issued">{bundle.course_ids.length} programmes</Stamp>
                    <span className="font-mono text-[9px] uppercase tracking-[0.11em] text-ink-2">{bundle.id}</span>
                  </div>
                  <h3 className="mt-3 text-[18px] font-semibold leading-snug tracking-[-0.02em] text-ink">{bundle.title}</h3>
                  <p className="mt-1.5 text-[12.5px] leading-5 text-ink-2">{bundle.description}</p>
                </div>
                <div className="mt-5 flex items-end justify-between gap-4 border-t border-rule pt-4">
                  <div>
                    <p className="tnum text-[18px] font-semibold tracking-[-0.02em] text-ink">₦{bundle.price_value.toLocaleString('en-NG')}</p>
                    {bundle.compare_at_price > bundle.price_value && (
                      <p className="tnum font-mono text-[10px] text-ink-2 line-through">₦{bundle.compare_at_price.toLocaleString('en-NG')}</p>
                    )}
                  </div>
                  <button type="button" onClick={() => handleBundleClick(bundle)} className="lms-btn-primary !min-h-[36px] !py-2 !text-[11.5px]">
                    Enroll in bundle
                  </button>
                </div>
              </Panel>
            ))}
          </div>
        </section>
      )}

      <section className="mt-10 overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-600 px-6 py-8 text-white shadow-xl shadow-blue-900/10 sm:px-9">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-blue-200">Admissions</p>
            <h2 className="mt-2 max-w-[28ch] text-[25px] font-semibold leading-tight tracking-[-0.03em]">
              Not sure which programme fits your work?
            </h2>
            <p className="mt-2 max-w-[58ch] text-[13px] leading-6 text-blue-100">
              Admissions can compare the pathways against your experience, time and export goal. No application fee,
              no obligation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Link to="/contact" className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-[12.5px] font-semibold text-blue-700 transition-colors hover:bg-blue-50">
              Book a conversation <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
            <a href="tel:+2349058681595" className="inline-flex min-h-[38px] items-center justify-center rounded-lg border border-white/25 px-4 py-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/10">
              Call the school
            </a>
          </div>
        </div>
      </section>

      {(selectedCourse || selectedBundle) && (
        <EnrollmentModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setSelectedCourse(null); setSelectedBundle(null); }}
          courseId={selectedCourse?.id}
          courseTitle={selectedCourse?.title}
          coursePrice={selectedCourse?.price}
          priceValue={selectedCourse?.priceValue}
          bundle={selectedBundle ? { id: selectedBundle.id, title: selectedBundle.title, price: `₦${selectedBundle.price_value.toLocaleString('en-NG')}`, priceValue: selectedBundle.price_value, courseIds: selectedBundle.course_ids } : undefined}
        />
      )}
    </div>
  );
};

export default Courses;
